import React, { useEffect, useState } from 'react'
import KontenCard from '../../customer/konten/KontenCard';
import CommonModal from "../../../component/model/ModelComponent";
import CommonButton from "../../../component/button/Button";
import InputComponent from "../../../component/input/Input";
import { FormControl, FormLabel, MenuItem, Select, FormHelperText, Typography, Grid } from "@mui/material";
import { t } from "i18next";
import { addKontenData, kontenChartcolor } from '../../../content/content';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { addSelectCustomerKonten, deleteSelectCustomerKonten, getSelectCustomerKonten, updateSelectCustomerKonten } from '../../../services/admin-services/selectCustomerKontenServices';
import Toast from '../../../component/toastify/Index';
import CommonChart from '../../../component/chart/CommonChart';
import Heading from '../../../component/Heading/Heading';
import { formatAmount, formatGermanAmount, formatPlainAmount, formatUpdateNumber, handleKeyPress } from "../../../helper/formatAmount";
import DeleteConfirmationModal from '../../../component/model/DeleteConfirmationModel';
import KontenNoDataPage from '../../customer/konten/NoDataPage';

const KontenComponent = ({ konten, setKonten, isModalOpen, setIsModalOpen, clientId }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [open, setOpen] = useState(false);
    const [debouncedKontostand, setDebouncedKontostand] = useState(formData.kontostand || '');
    const [timer, setTimer] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'kontostand') {
            // Immediately set the raw value in the form state to avoid losing user input while typing
            setFormData((prev) => ({ ...prev, [name]: value }));

            // Clear the previous timer if it's still active
            if (timer) {
                clearTimeout(timer);
            }

            // Set a new timer for debouncing the formatted value update
            const newTimer = setTimeout(() => {
                // Format the value only after the debounce delay
                const formattedValue = formatGermanAmount(value);
                setDebouncedKontostand(formattedValue);  // Update debounced value after the delay
            }, 500); // 500 ms debounce delay

            setTimer(newTimer);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    useEffect(() => {
        const debounceTime = setTimeout(() => {
            setFormData((prev) => ({ ...prev, kontostand: debouncedKontostand }));
        }, 1000)
        return () => clearTimeout(debounceTime)
    }, [debouncedKontostand])

    const getKonten = async () => {
        const response = await getSelectCustomerKonten(clientId);
        if (response?.isSuccess) {
            setKonten([...response.customerKonten])
        }
    }

    useEffect(() => {
        getKonten()
    }, [])

    const validate = () => {
        const newErrors = {};
        let valid = true;

        addKontenData.forEach((item) => {
            const value = String(formData[item.name])?.trim();
            if (!value || value === "undefined") {
                newErrors[item.name] = `${t(item.translationKey)} ${t("common_is_required")}`;
                valid = false;
            } else if (item.name === "iban") {
                if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(formData.iban)) {
                    newErrors[item.name] = "IBAN should be a valid IBAN format";
                    valid = false;
                } else if (formData.iban.length > 22) {
                    newErrors[item.name] = t('IBAN_22_characters_long_error');
                    valid = false;

                }
            }
        });

        setErrors(newErrors);
        return valid;
    };

    const handleEdit = (item) => {
        const kontostandFormat = formatUpdateNumber(item.kontostand);
        setFormData({ ...item, kontostand: kontostandFormat })
        setDebouncedKontostand(kontostandFormat)
        setErrors({})
        setIsModalOpen(true);
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validate()) return;

        formData.kontostand = formatPlainAmount(formData.kontostand)

        let response = [];
        if (formData._id) {
            response = await updateSelectCustomerKonten(formData, clientId);
        }
        else {
            formData.isManual = true;
            response = await addSelectCustomerKonten(formData, clientId);
        }
        if (response?.isSuccess) {
            Toast(response?.message, "success");
            setIsModalOpen(false)
            getKonten();
            setFormData({})
        }
    }

    const handleDelete = async () => {
        const payload = {
            ...formData,
            kontostand: formatPlainAmount(formData?.kontostand),
        }
        const response = await deleteSelectCustomerKonten(payload, clientId);
        if (response?.isSuccess) {
            Toast(response?.message, "success")
            getKonten();
            setFormData({})
            setOpen(false);
            setIsModalOpen(false)
        }
    }

    const option = {
        chart: {
            type: "donut",
            height: "100%"
        },
        series: konten.map((item) => item.kontostand),
        labels: konten.map((item) => item.bank_name),
        colors: kontenChartcolor,
        fill: {
            type: "gradient",
        },
        legend: {
            position: "bottom",
            horizontalAlign: "center",
        },
        dataLabels: {
            enabled: false,
        },
        stroke: { width: 0 },
        plotOptions: {
            pie: {
                expandOnClick: false,
                donut: {
                    size: "80%",
                    labels: {
                        show: true,
                        name: {
                            show: false,
                        },
                        value: {
                            show: true,
                            showAlways: true,
                            label: "Total Amount",
                            fontWeight: 500,
                            fontSize: '40px',
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: "Total Amount",
                            fontWeight: 500,
                            formatter: function (w) {
                                return (
                                    formatAmount(
                                        w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                                    ) + " €"
                                );
                            },
                        },
                    },
                },
            },
        },
        states: {
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: "none",
                },
            },
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: function (val, opts) {
                    return formatAmount(val) + " €";
                },
            },
        },

        responsive: [
            {
                breakpoint: 10000,
                options: {
                    chart: {
                        height: "1000vh",
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.5vh',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        fontSize: "50px",
                        markers: {
                            size: 20,
                        },
                        itemMargin: {
                            vertical: 20,
                            horizontal: 20
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1.6vh",
                        },
                    },
                },
            },
            {
                breakpoint: 6000,
                options: {
                    chart: {
                        height: "700vh"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.5vh',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        fontSize: "30px",
                        padding: "20px",
                        markers: {
                            size: 15,
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1.5vh",
                        },
                    },
                },
            },
            {
                breakpoint: 5000,
                options: {
                    chart: {
                        height: "400vh"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.2vh',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        fontSize: "20px",
                        markers: {
                            size: 10,
                        },
                        itemMargin: {
                            vertical: 10,
                            horizontal: 10
                        },
                    },

                    tooltip: {
                        style: {
                            fontSize: "1.3vh",
                        },
                    },
                },
            },
            {
                breakpoint: 3000,
                options: {
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '2vh',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        fontSize: "18px",
                        markers: {
                            size: 8,
                        },
                        itemMargin: {
                            vertical: 8,
                            horizontal: 8
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1.5vh",
                        },
                    },
                },
            },
            {
                breakpoint: 2500,
                options: {
                    chart: {
                        height: "350vh"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.5vh',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        fontSize: "16px",
                        markers: {
                            size: 8,
                        },
                        itemMargin: {
                            vertical: 5,
                            horizontal: 5
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1.2vh",
                        },
                    },
                },
            },
            {
                breakpoint: 2000,
                options: {
                    chart: {
                        height: "280vh"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.2vh',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        fontSize: "11px",
                        markers: {
                            size: 5,
                        },
                        itemMargin: {
                            vertical: 5,
                            horizontal: 5
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1.5vh",
                        },
                    },
                },
            },
            {
                breakpoint: 1024,
                options: {
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '16px',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "9px",
                        },
                    },
                },
            },

            {
                breakpoint: 480,
                options: {
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '10px',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        fontSize: "10px",
                        markers: {
                            size: 4,
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "5px",
                        },
                    },

                },
            },
        ],
    };

    return (
        <div className="konten-content">
            <DeleteConfirmationModal open={open}
                onClose={() => setOpen(false)}
                onClick={handleDelete}
            />
            <CommonModal
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setFormData({}); setErrors({}); setDebouncedKontostand(null) }}
                title="Konten"
                className=""
            >
                <Typography
                    className="user-model"
                    sx={{
                        border: "1px solid #E3E3E3",
                        padding: "20px",
                        borderRadius: "10px",
                    }}
                >
                    <form onSubmit={handleSubmit} className="user-form">
                        <div className="konten-form-data">
                            {addKontenData.map((item) => (
                                item.type === "select" ? (
                                    <FormControl fullWidth error={!!errors[item.name]}>
                                        <FormLabel
                                            className="label"
                                            sx={{
                                                paddingTop: "1.25rem",
                                                paddingBottom: "10px",
                                                display: "inline-block",
                                                color: "#000",
                                            }}
                                        >
                                            {t(item.translationKey)}
                                        </FormLabel>
                                        <Select
                                            className="select-input"
                                            fullWidth
                                            value={formData[item?.name] || ""}
                                            onChange={handleChange}
                                            displayEmpty
                                            inputProps={{
                                                name: item?.name,
                                                IconComponent: () => (
                                                    <span
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            paddingRight: "10px",
                                                        }}
                                                    >
                                                        <ExpandMoreIcon sx={{ fontSize: "30px", color: "#33333399" }} />
                                                    </span>
                                                ),
                                            }}
                                            sx={{
                                                fontSize: "14px",
                                                color: "#313131",
                                                fontFamily: "poppins"
                                            }}
                                            style={{
                                                padding: "1px",
                                                borderRadius: "10px",
                                                border: "1px solid #e3e3e391",
                                            }}
                                            disabled={item?.name === "year"}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 150,
                                                        overflowY: 'auto',
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled
                                                sx={{
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {t(item.placeholderTranslationKey)}
                                            </MenuItem>
                                            {item?.options?.map((option, i) => (
                                                <MenuItem key={i} value={option.value} disabled={option.value === "2023"}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors[item.name] && (
                                            <FormHelperText
                                                sx={{
                                                    color: "#d32f2f",
                                                    margin: "5px 0 0 0 ",
                                                    fontSize: "14px",
                                                    fontFamily: "Poppins",
                                                }}
                                            >
                                                {errors[item.name]}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                ) : (
                                    <InputComponent
                                        key={item?.name}
                                        borderColor="E3E3E3"
                                        border="2px solid #E3E3E3"
                                        padding="9px 12px"
                                        type={item.type}
                                        placeholder={t(item.placeholderTranslationKey)}
                                        label={t(item.translationKey)}
                                        className="user-input"
                                        name={item?.name}
                                        value={formData[item?.name] || ""}
                                        error={!!errors[item.name]}
                                        helperText={errors[item.name]}
                                        onChange={handleChange}
                                        handleKeyPress={item?.handleKeyPress ? handleKeyPress : null}
                                    />
                                )
                            )
                            )
                            }

                        </div>
                        <Typography className="model-action-btn">
                            <CommonButton
                                text={formData._id ? "Delete" : t("user_clear_all")}
                                className="invitation-btn konten-btn"
                                bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
                                onClick={formData._id ? () => setOpen(true) : () => { setFormData({}); setDebouncedKontostand(null); }}
                            />
                            <CommonButton
                                type="submit"
                                text={formData._id ? "Update" : "Save"}
                                className="add-user-btn konten-btn"
                            />
                        </Typography>
                    </form>
                </Typography>
            </CommonModal>

            {
                konten?.length > 0
                    ? <Grid container rowSpacing={{ xs: 2, sm: 2, md: 3 }} columnSpacing={{ xs: 1, sm: 2, md: 3 }} className="">
                        <Grid size={{ xs: 12, md: 6, lg: 8, xl: 9 }} className="konten-card-order konten-cards">
                            <Grid container rowSpacing={{ xs: 2, sm: 2, md: 3 }} columnSpacing={{ xs: 1, sm: 2, md: 3 }} className="">
                                <KontenCard konten={konten} handleEdit={handleEdit} setFormData={setFormData} formData={formData} size={{ xs: 12, md: 12, lg: 6, xl: 4, xxl: 3, xxxl: 2 }} />
                            </Grid>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }} className="konten-chart">
                            <div className="donut">
                                <Heading text={t('koten_summe')} className="konten-chart-heading" />
                                <CommonChart options={option} series={option.series} type="donut" />
                            </div>
                        </Grid>
                    </Grid>
                    : <KontenNoDataPage />
            }
        </div>
    )
}

export default KontenComponent
